package com.sddamico.swigmore.components.pwa

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.sddamico.swigmore.api.PollApi
import com.sddamico.swigmore.ds.RevealStyle
import com.sddamico.swigmore.ds.feedback.Banner
import com.sddamico.swigmore.ds.forms.Button
import com.sddamico.swigmore.ds.forms.ButtonStyle
import com.sddamico.swigmore.ds.forms.ButtonVariant
import com.sddamico.swigmore.models.PollRules
import com.sddamico.swigmore.models.PushSubscribeRequest
import com.sddamico.swigmore.util.BrowserPushSubscription
import com.sddamico.swigmore.util.PushPermissionDeniedException
import com.sddamico.swigmore.util.andList
import com.sddamico.swigmore.util.currentPushSubscription
import com.sddamico.swigmore.util.ensureServiceWorker
import com.sddamico.swigmore.util.forgetOnSignOut
import com.sddamico.swigmore.util.minuteClock
import com.sddamico.swigmore.util.isPushSupported
import com.sddamico.swigmore.util.notificationPermission
import com.sddamico.swigmore.util.subscribeToPush
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.localStorage
import kotlinx.coroutines.launch
import kotlin.js.Date
import org.jetbrains.compose.web.dom.Div

private const val SNOOZE_KEY = "swigmore:push-snoozed-until"
private const val SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000L

// Whether the last page showed the banner, so the next page starts with it instead of pushing its
// content down once the checks below finish. It belongs to the account that saw it.
private object LastPage {
  var showedBanner = false

  init {
    forgetOnSignOut { showedBanner = false }
  }
}

/**
 * Asks a signed-in owner to turn on push for this device, and keeps an already-granted browser
 * enrolled. Shows nothing when push is unsupported, blocked, already on, or snoozed.
 */
@Composable
fun PushOptIn(modifier: Modifier = Modifier) {
  val scope = rememberCoroutineScope()
  var visible by remember { mutableStateOf(LastPage.showedBanner) }
  val arrived = remember { !LastPage.showedBanner }
  SideEffect { LastPage.showedBanner = visible }
  var enabling by remember { mutableStateOf(false) }
  var error by remember { mutableStateOf<String?>(null) }
  // Preloaded so the click has no network await before requestPermission()/subscribe(): Android
  // Chrome drops the user activation across one and the subscribe silently fails.
  var vapidKey by remember { mutableStateOf<String?>(null) }

  /** Enrolls this browser where it can without asking; true when the owner still has to opt in. */
  suspend fun needsOptIn(): Boolean {
    if (!isPushSupported() || notificationPermission() == "denied") return false
    val key = try {
      val key = PollApi.vapidKey() ?: return false
      ensureServiceWorker()
      vapidKey = key
      val current = currentPushSubscription()
      if (current != null) {
        saveSubscription(current)
        return false
      }
      key
    } catch (_: Throwable) {
      return false
    }
    // Signing out un-enrolls the browser but leaves the permission granted, so re-enroll quietly. A
    // granted browser whose subscribe failed is still not enrolled, so it gets the banner again.
    if (notificationPermission() == "granted") {
      try {
        saveSubscription(subscribeToPush(key))
        return false
      } catch (e: Throwable) {
        error = enrollErrorText(e)
      }
    }
    val snoozedUntil = localStorage.getItem(SNOOZE_KEY)?.toDoubleOrNull() ?: 0.0
    return Date.now() >= snoozedUntil
  }

  LaunchedEffect(Unit) { visible = needsOptIn() }

  if (!visible) return

  Div(attrs = (if (arrived) RevealStyle.toModifier() else Modifier).then(modifier).toAttrs()) {
    Banner(
      title = "Get the poll on your phone.",
      body = error ?: "A push when the poll is out, and nudges Friday at ${andList(PollRules.REMINDER_LEAD_MINUTES.map(::reminderClock))} ET if you haven't voted.",
      actions = {
        Button("Not now", variant = ButtonVariant.Ghost, style = ButtonStyle.CapsSm, onClick = {
          localStorage.setItem(SNOOZE_KEY, (Date.now() + SEVEN_DAYS_MS).toString())
          visible = false
        })
        val key = vapidKey
        Button("Turn on push", variant = ButtonVariant.Field, style = ButtonStyle.CapsSm, disabled = enabling || key == null, onClick = {
          if (key != null) scope.launch {
            enabling = true
            error = null
            try {
              saveSubscription(subscribeToPush(key))
              localStorage.removeItem(SNOOZE_KEY)
              visible = false
            } catch (_: PushPermissionDeniedException) {
              visible = false
            } catch (e: Throwable) {
              error = enrollErrorText(e)
            }
            enabling = false
          }
        })
      },
    )
  }
}

/**
 * Chrome rejects subscribe() with an AbortError ("Registration failed - push service error") when it
 * can't register with its push service, which is the browser's network or settings, not this app.
 */
private fun enrollErrorText(e: Throwable): String =
  if (e.asDynamic().name == "AbortError") {
    "Your browser couldn't reach its push service, so push isn't on yet. Try again later, or on another network or browser."
  } else {
    "Couldn't turn on push: ${e.message ?: "unknown error"}"
  }

private suspend fun saveSubscription(subscription: BrowserPushSubscription) =
  PollApi.subscribePush(PushSubscribeRequest(subscription.endpoint, subscription.p256dh, subscription.auth))

private fun reminderClock(leadMinutes: Int) = minuteClock(PollRules.reminderMinuteOfDay(leadMinutes))
